import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import axios from 'axios';
import type {
  CreateDocumentPatch,
  CreateDocumentTemplateData,
  InitialDocument,
} from '../types/create-document-types';
import { IntegrationsQueries } from '../../settings/integrations/graphql/integrations.queries';
import { CreateDocumentMutations } from './create-document.mutations';
import { CreateDocumentQueries } from './create-document.queries';

export class CreateDocumentService {
  static async createDocument(documentType: string): Promise<string> {
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-type', documentType);
    const {
      data: { document_id },
    } = await axios.post('/save/document', body);
    return document_id;
  }

  static async getInitialDocument(documentId: string): Promise<InitialDocument> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: CreateDocumentQueries.initialDocument,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static updateDocument = async (
    documentId: string,
    patch: Partial<CreateDocumentPatch>,
    settings = {}
  ): Promise<Document> => {
    const {
      data: { updateDocument },
    } = await apolloClientInternal.mutate({
      mutation: CreateDocumentMutations.updateDocument,
      variables: {
        patch,
        settings,
        documentId,
      },
    });
    return updateDocument;
  };

  static async getTemplate(documentId: string): Promise<CreateDocumentTemplateData> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: CreateDocumentQueries.templateDocument,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async implementTemplate(
    documentId: string,
    templateId: string,
    pageOffset: number
  ): Promise<CreateDocumentPatch> {
    const {
      data: { implementTemplateInDocument },
    } = await apolloClientInternal.mutate({
      mutation: CreateDocumentMutations.implementTemplate,
      variables: {
        documentId,
        templateId,
        pageOffset,
      },
    });
    return implementTemplateInDocument;
  }

  static async implementResource(payload: {
    documentId: string;
    resourceId: string;
    contentId: string;
  }): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: CreateDocumentMutations.implementResource,
      variables: { payload },
    });
  }

  static async loadGoogleDocs(): Promise<boolean> {
    const {
      data: { integration },
    } = await apolloClientInternal.query({
      query: IntegrationsQueries.googleDocs,
      variables: { name: 'docs' },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return integration?.status === 'connected';
  }
}
